import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';

import { getBackendUrl } from '../utils/api';
const API_URL = getBackendUrl();

// WebAuthn helper functions
const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const base64urlToBuffer = (base64url) => {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const Login = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login, checkAuth } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(true); // Show by default, check on click
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [setupEmail, setSetupEmail] = useState('');
  const [terminatedMessage, setTerminatedMessage] = useState(false);

  // Check for terminated user redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('terminated') === 'true') {
      setTerminatedMessage(true);
      // Clean URL
      window.history.replaceState({}, document.title, '/login');
    }
  }, []);

  // Biometric is always shown - actual support is checked when user clicks

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('success'));
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // WebAuthn Login (Face ID / Touch ID)
  const handleBiometricLogin = async () => {
    // Check WebAuthn support on click
    if (!window.PublicKeyCredential) {
      toast.error(t('biometricNotSupported'));
      return;
    }

    setBiometricLoading(true);
    try {
      // Step 1: Get authentication options from server
      const optionsRes = await axios.post(`${API_URL}/api/auth/webauthn/login/start`, {
        email: null // Allow any registered credential
      }, { withCredentials: true });

      const options = optionsRes.data;

      // Convert base64url to ArrayBuffer
      options.challenge = base64urlToBuffer(options.challenge);
      
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map(cred => ({
          ...cred,
          id: base64urlToBuffer(cred.id)
        }));
      }

      // Step 2: Get credential from device (triggers Face ID / Touch ID)
      const credential = await navigator.credentials.get({
        publicKey: options
      });

      // Step 3: Send credential to server for verification
      const credentialData = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          authenticatorData: bufferToBase64url(credential.response.authenticatorData),
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          signature: bufferToBase64url(credential.response.signature),
          userHandle: credential.response.userHandle ? bufferToBase64url(credential.response.userHandle) : null
        }
      };

      const loginRes = await axios.post(`${API_URL}/api/auth/webauthn/login/finish`, {
        credential: credentialData
      }, { withCredentials: true });

      if (loginRes.data.user) {
        // Save session token to localStorage (same as regular login)
        if (loginRes.data.session_token) {
          localStorage.setItem('session_token', loginRes.data.session_token);
        }
        await checkAuth();
        toast.success(t('biometricLoginSuccess'));
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Biometric login error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error(t('biometricCancelled'));
      } else if (error.name === 'InvalidStateError') {
        // No credentials registered on this device
        toast.error(t('noBiometricRegistered'));
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t('biometricLoginFailed'));
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  // WebAuthn Registration (Setup Face ID / Touch ID)
  const handleBiometricSetup = async () => {
    if (!setupEmail) {
      toast.error(t('enterEmail'));
      return;
    }

    setBiometricLoading(true);
    try {
      // Step 1: Get registration options from server
      const optionsRes = await axios.post(`${API_URL}/api/auth/webauthn/register/start`, {
        email: setupEmail
      }, { withCredentials: true });

      const options = optionsRes.data;

      // Convert base64url to ArrayBuffer
      options.challenge = base64urlToBuffer(options.challenge);
      options.user.id = base64urlToBuffer(options.user.id);
      
      if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map(cred => ({
          ...cred,
          id: base64urlToBuffer(cred.id)
        }));
      }

      // Step 2: Create credential on device (triggers Face ID / Touch ID setup)
      const credential = await navigator.credentials.create({
        publicKey: options
      });

      // Step 3: Send credential to server for storage
      const credentialData = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          attestationObject: bufferToBase64url(credential.response.attestationObject),
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON)
        }
      };

      await axios.post(`${API_URL}/api/auth/webauthn/register/finish`, {
        email: setupEmail,
        credential: credentialData
      }, { withCredentials: true });

      toast.success(t('biometricSetupSuccess'));
      setShowBiometricSetup(false);
      setSetupEmail('');
    } catch (error) {
      console.error('Biometric setup error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error(t('biometricCancelled'));
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t('biometricSetupFailed'));
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen relative" 
      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
      style={{
        background: 'linear-gradient(180deg, #1E40AF 0%, #1E40AF 20%, #2563EB 26%, #3B82F6 34%, #C4607A 44%, #F0D8DE 52%, #FFFFFF 60%, #FFFFFF 100%)',
        minHeight: '100vh',
        minHeight: '-webkit-fill-available'
      }}
    >
      {/* Animated Gradient Circles - Maroon Theme */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large main circle - top */}
        <div 
          className="login-float-slow"
          style={{
            position: 'absolute',
            top: '-10%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '450px',
            height: '450px',
            borderRadius: '50%',
            background: 'radial-gradient(circle at 30% 30%, rgba(128,0,32,0.15) 0%, rgba(128,0,32,0.08) 30%, rgba(128,0,32,0.03) 50%, transparent 70%)',
            filter: 'blur(2px)',
          }}
        />
        
        {/* Secondary circle - right */}
        <div 
          className="login-float-medium"
          style={{
            position: 'absolute',
            top: '20%',
            right: '-15%',
            width: '350px',
            height: '350px',
            borderRadius: '50%',
            background: 'radial-gradient(circle at 40% 40%, rgba(128,0,32,0.12) 0%, rgba(128,0,32,0.06) 40%, transparent 70%)',
            filter: 'blur(1px)',
          }}
        />
        
        {/* Third circle - left */}
        <div 
          className="login-float-reverse"
          style={{
            position: 'absolute',
            top: '40%',
            left: '-20%',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'radial-gradient(circle at 60% 40%, rgba(128,0,32,0.1) 0%, rgba(128,0,32,0.05) 40%, transparent 70%)',
            filter: 'blur(1px)',
          }}
        />
        
        {/* Fourth circle - bottom right */}
        <div 
          className="login-float-slow"
          style={{
            position: 'absolute',
            bottom: '5%',
            right: '-10%',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'radial-gradient(circle at 30% 50%, rgba(128,0,32,0.12) 0%, rgba(128,0,32,0.06) 40%, transparent 70%)',
            filter: 'blur(2px)',
          }}
        />
        
        {/* Fifth circle - bottom left */}
        <div 
          className="login-float-medium"
          style={{
            position: 'absolute',
            bottom: '15%',
            left: '5%',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: 'radial-gradient(circle at 50% 30%, rgba(128,0,32,0.08) 0%, rgba(128,0,32,0.04) 50%, transparent 70%)',
            filter: 'blur(1px)',
          }}
        />

        {/* Gold accent circle */}
        <div 
          className="login-float-reverse"
          style={{
            position: 'absolute',
            top: '60%',
            right: '15%',
            width: '180px',
            height: '180px',
            borderRadius: '50%',
            background: 'radial-gradient(circle at 40% 40%, rgba(189,155,87,0.15) 0%, rgba(189,155,87,0.05) 50%, transparent 70%)',
            filter: 'blur(2px)',
          }}
        />

        {/* Small floating particles - Maroon theme */}
        <div className="absolute top-[25%] right-[20%] w-3 h-3 bg-[#1E40AF]/20 rounded-full login-pulse-slow" />
        <div className="absolute top-[45%] left-[15%] w-2 h-2 bg-[#1E40AF]/25 rounded-full login-pulse-medium" />
        <div className="absolute bottom-[30%] right-[30%] w-2 h-2 bg-[#3B82F6]/30 rounded-full login-pulse-slow" />
      </div>

      {/* Top Buttons - Floating without background */}
      <div 
        className="absolute top-0 left-0 right-0 z-20 px-5 flex justify-between items-center"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <button 
          onClick={() => navigate('/welcome')}
          className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"
        >
          <svg className="w-5 h-5 text-white rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <button
          onClick={toggleLanguage}
          className="h-10 px-4 text-sm font-semibold text-white bg-white/20 backdrop-blur-sm rounded-full"
        >
          {i18n.language === 'en' ? 'العربية' : 'English'}
        </button>
      </div>

      {/* Main Content */}
      <div 
        className="relative z-10 px-5 pb-5"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 60px)' }}
      >
        {/* Logo Section - Just HELCOO Text */}
        <div className="text-center mb-5">
          <h1 
            className="font-black mb-1 text-white"
            style={{
              fontSize: '3.3rem',
              fontFamily: 'Inter, sans-serif',
              textShadow: '0 2px 10px rgba(0,0,0,0.2)',
              direction: 'ltr'
            }}
          >
            HELCOO
          </h1>
          <p 
            className="font-semibold text-white/80"
            style={{
              fontSize: '1.04rem'
            }}
          >
            {t('hrManagementSystem')}
          </p>
        </div>

        {/* Login Card */}
        <div 
          className="w-full max-w-md mx-auto rounded-3xl p-5 flex-1 flex flex-col"
          style={{
            background: 'rgba(255,255,255,0.98)',
            border: '1px solid rgba(128,0,32,0.1)',
            boxShadow: '0 10px 40px rgba(128,0,32,0.1)'
          }}
        >
          <h2 className="text-xl font-bold text-[#1E40AF] mb-4 text-center">{t('login')}</h2>
          
          {/* Terminated Employee Message */}
          {terminatedMessage && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center gap-2 text-red-600">
                <span className="text-xl">⛔</span>
                <div>
                  <p className="font-bold">
                    {i18n.language === 'ar' ? 'تم إنهاء خدماتك' : 'Your employment has been terminated'}
                  </p>
                  <p className="text-sm mt-1 text-red-500">
                    {i18n.language === 'ar' 
                      ? 'لا يمكنك الوصول إلى النظام. يرجى مراجعة قسم الموارد البشرية.'
                      : 'You cannot access the system. Please contact HR department.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-2">
                {t('email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3.5 rounded-xl text-gray-900 text-base font-medium placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40"
                style={{
                  background: '#F5F5F5',
                  border: '2px solid rgba(128,0,32,0.15)'
                }}
                placeholder={t('enterEmail')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-800 mb-2">
                {t('password')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3.5 rounded-xl text-gray-900 text-base font-medium placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/40 ltr:pr-12 rtl:pl-12"
                  style={{
                    background: '#F5F5F5',
                    border: '2px solid rgba(128,0,32,0.15)'
                  }}
                  placeholder={t('enterPassword')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute ltr:right-3 rtl:left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-xl font-bold text-lg text-white transition-all active:scale-[0.98] disabled:opacity-70"
              style={{
                background: 'linear-gradient(135deg, #1E40AF 0%, #A0153E 100%)',
                boxShadow: '0 8px 25px rgba(128,0,32,0.35)'
              }}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  {t('loading')}
                </div>
              ) : t('login')}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t-2 border-gray-200"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 bg-white text-gray-500 text-sm font-semibold">{t('or')}</span>
            </div>
          </div>

          {/* New Employee - Join Company with Code */}
          <button
            onClick={() => navigate('/employee-code-login')}
            className="w-full text-center py-4 px-4 rounded-xl transition-all active:scale-[0.98] hover:shadow-lg"
            style={{ 
              background: 'linear-gradient(135deg, #FFF9F9 0%, #FFF5F5 100%)',
              border: '2px solid rgba(128,0,32,0.25)',
              boxShadow: '0 4px 15px rgba(128,0,32,0.1)'
            }}
          >
            <p className="text-gray-700 text-sm mb-1">
              {i18n.language === 'ar' ? 'هل لديك كود انضمام من شركتك؟' : 'Do you have a join code from your company?'}
            </p>
            <div className="text-[#1E40AF] font-bold text-base flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              {i18n.language === 'ar' ? 'انضم لشركتك بالكود' : 'Join your company with code'}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>

          {/* Biometric Login */}
          {biometricSupported && (
            <button
              onClick={handleBiometricLogin}
              disabled={biometricLoading}
              className="w-full flex items-center justify-center gap-3 py-3 rounded-xl font-semibold text-base transition-all mt-3"
              style={{
                background: '#F5F5F5',
                border: '2px solid rgba(128,0,32,0.15)',
                color: '#1E40AF'
              }}
            >
              {biometricLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1E40AF]"></div>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                </svg>
              )}
              {t('loginWithBiometric')}
            </button>
          )}

          {/* Links Row */}
          <div className="mt-4 flex items-center justify-between">
            <Link to="/forgot-password" className="text-gray-600 font-semibold text-sm hover:text-[#1E40AF]">
              {t('forgotPassword')}
            </Link>
            <Link to="/register" className="text-[#1E40AF] font-bold text-sm hover:text-[#A0153E]">
              {t('register')}
            </Link>
          </div>

          {/* Biometric Setup */}
          {biometricSupported && (
            <button
              onClick={() => setShowBiometricSetup(true)}
              className="mt-3 w-full text-center text-sm text-gray-500 font-medium hover:text-[#1E40AF]"
            >
              {t('setupBiometric')}
            </button>
          )}
        </div>

        {/* Footer - Made by HELCOO */}
        <div className="mt-6 text-center">
          <p className="text-gray-400 text-xs font-medium">Made with ❤️ by</p>
          <p 
            className="text-lg font-black"
            style={{
              background: 'linear-gradient(135deg, #1E40AF 0%, #A0153E 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            HELCOO
          </p>
        </div>
      </div>

      {/* Custom animations */}
      <style>{`
        @keyframes login-float-slow {
          0%, 100% { transform: translateX(-50%) translateY(0px); }
          50% { transform: translateX(-50%) translateY(-25px); }
        }
        @keyframes login-float-medium {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-15px) scale(1.03); }
        }
        @keyframes login-float-reverse {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(15px) scale(0.97); }
        }
        @keyframes login-pulse-slow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.5); }
        }
        @keyframes login-pulse-medium {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.3); }
        }
        .login-float-slow { animation: login-float-slow 8s ease-in-out infinite; }
        .login-float-medium { animation: login-float-medium 6s ease-in-out infinite; }
        .login-float-reverse { animation: login-float-reverse 7s ease-in-out infinite; }
        .login-pulse-slow { animation: login-pulse-slow 4s ease-in-out infinite; }
        .login-pulse-medium { animation: login-pulse-medium 3s ease-in-out infinite; }
      `}</style>

      {/* Biometric Setup Modal */}
      {showBiometricSetup && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-[#1E40AF] text-white px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                </svg>
                {t('setupBiometricTitle')}
              </h3>
              <button
                onClick={() => setShowBiometricSetup(false)}
                className="text-white hover:text-white/80 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4 text-center">
                {t('setupBiometricDescription')}
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {t('email')}
                </label>
                <input
                  type="email"
                  value={setupEmail}
                  onChange={(e) => setSetupEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#1E40AF]/30 focus:border-[#1E40AF] transition-all"
                  placeholder={t('enterEmail')}
                />
              </div>

              <button
                onClick={handleBiometricSetup}
                disabled={biometricLoading || !setupEmail}
                className="w-full bg-[#1E40AF] text-white py-3 rounded-xl font-bold hover:bg-[#A0153E] disabled:bg-gray-400 transition-all flex items-center justify-center gap-2"
              >
                {biometricLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                  </svg>
                )}
                {t('setupBiometricButton')}
              </button>

              <p className="mt-4 text-xs text-gray-500 text-center">
                {t('setupBiometricNote')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
